import type { FC } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import ReactCodeMirror from '@uiw/react-codemirror';
import { wellPilotTheme } from '../utils';
import { linter, lintGutter } from '@codemirror/lint';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { RuleEditorContainer } from './index';
import { useCurrentTasksFormStore, useRuleForEditor } from '../hooks';
import { Close as CloseIcon } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import type { BasicFormValues } from '../facebook/types';
import { notificationChannelOptions } from '../api';
import { observer } from 'mobx-react-lite';
import type { BasicGoogleValues } from '../google/components/EditGoogleRule';
import { useCurrentNetwork } from '../../../hooks';
import useTimetableStore from '../hooks/useTimetableStore';
import useFilterStore from '../hooks/useFilterStore';
import type { GoogleRuleDetails } from '../google/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import type { LevelValue, RuleEntry } from '../types';
import { omit } from 'lodash-es';

interface RuleScriptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  rule: RuleEntry | GoogleRuleDetails;
}

const useRuleObject = (rule: RuleScriptDialogProps['rule']) => {
  const { data: notificationChannelsList } = useSuspenseQuery(notificationChannelOptions);

  const network = useCurrentNetwork();

  const { getValues } = useFormContext<BasicFormValues | BasicGoogleValues>();
  const timeTableStore = useTimetableStore();
  const filterStore = useFilterStore();
  const tasksFormStore = useCurrentTasksFormStore();

  if (network === 'facebook') {
    const { name, level, description, notificationChannels, notificationLevel, facebookAdAccountIds } =
      getValues() as BasicFormValues;

    const getTransformedNotificationChannels = () => {
      return notificationChannelsList.filter((nc) => notificationChannels.includes(nc.id));
    };

    return {
      ...omit(rule.data, timeTableStore.scheduleInterval ? 'timetable' : 'schedule_interval'),
      filters: filterStore.filterGroups,
      ...(timeTableStore.scheduleInterval
        ? { schedule_interval: timeTableStore.scheduleInterval }
        : { timetable: timeTableStore.timeTableDTO }),
      tasks: tasksFormStore.tasksFormValues,
      name,
      description,
      level: level as LevelValue,
      notification_channels: getTransformedNotificationChannels(),
      facebook_ad_account_ids: facebookAdAccountIds.map(String),
      notification_level: notificationLevel,
    };
  }

  if (network === 'google') {
    const { name, ruleStatus, level, description, adAccountIds, type, project } = getValues() as BasicGoogleValues;

    return {
      ...rule,
      name,
      status: ruleStatus,
      description,
      data: {
        ...omit(rule.data, timeTableStore.scheduleInterval ? 'timetable' : 'schedule_interval'),
        type,
        tasks: tasksFormStore.tasksFormValues,
        ...(timeTableStore.scheduleInterval
          ? { schedule_interval: timeTableStore.scheduleInterval }
          : { timetable: timeTableStore.timeTableDTO }),
        filters: filterStore.filterGroups,
        ad_account_ids: adAccountIds,
        // notification_message: notificationMessage,
        level: level as LevelValue,
      },
    };
  }

  return {};
};

const RuleScriptDialog: FC<RuleScriptDialogProps> = observer(({ isOpen, onClose, rule }) => {
  const network = useCurrentNetwork();

  const ruleObj = useRuleObject(rule);

  const { code, setCode } = useRuleForEditor({
    content: ruleObj,
    network,
  });

  return (
    <Dialog fullWidth maxWidth="lg" open={isOpen} onClose={onClose}>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          id="rule-script-dialog-title"
        >
          <Typography variant="subtitle1">rule script</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <RuleEditorContainer>
          <ReactCodeMirror
            value={code}
            editable={false}
            theme={wellPilotTheme}
            height={`${window.screen.height * 0.7}px`}
            extensions={[lintGutter(), json(), linter(jsonParseLinter())]}
            onChange={setCode}
          />
        </RuleEditorContainer>
      </Box>
    </Dialog>
  );
});

export default RuleScriptDialog;
